import { lazy, Suspense } from 'react';
import { Routes } from 'react-router-dom';
import { Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { MotionConfig } from 'framer-motion';
import { Layout } from './components/layout/Layout.jsx';
import { ScrollToTop } from './components/layout/ScrollToTop.jsx';
import { ConferenceConfigProvider } from './context/ConferenceConfigContext.jsx';
import { usePageTracking } from './hooks/usePageTracking.js';
// Home is loaded eagerly — it is the landing route and owns the LCP hero, so we
// never want it gated behind an extra chunk request.
import Home from './pages/Home.jsx';

// Every other public page is split into its own chunk and fetched on demand.
// This keeps the initial download to the shell + Home, which is the single
// biggest win for first-load speed on shared hosting (cPanel).
const Speakers = lazy(() => import('./pages/Speakers.jsx'));
const Publications = lazy(() => import('./pages/Publications.jsx'));
const Cfp = lazy(() => import('./pages/Cfp.jsx'));
const Registration = lazy(() => import('./pages/Registration.jsx'));
const OfferRegistration = lazy(() => import('./pages/OfferRegistration.jsx'));
const AcceptedPaperRegistration = lazy(() => import('./pages/AcceptedPaperRegistration.jsx'));
const Attendance = lazy(() => import('./pages/Attendance.jsx'));
const ContactUs = lazy(() => import('./pages/ContactUs.jsx'));
const Testimonials = lazy(() => import('./pages/Testimonials.jsx'));
const JoinUs = lazy(() => import('./pages/JoinUs.jsx'));
const Committees = lazy(() => import('./pages/Committees.jsx'));
const Venue = lazy(() => import('./pages/Venue.jsx'));
const Gallery = lazy(() => import('./pages/Gallery.jsx'));
const Agenda = lazy(() => import('./pages/Agenda.jsx'));
const PaperSubmission = lazy(() => import('./pages/PaperSubmission.jsx'));
const ParticipationForm = lazy(() => import('./pages/ParticipationForm.jsx'));
const AuthorGuidelines = lazy(() => import('./pages/AuthorGuidelines.jsx'));
const Terms = lazy(() => import('./pages/Terms.jsx'));
const LegalInformation = lazy(() => import('./pages/LegalInformation.jsx'));
const ParticipationPolicy = lazy(() => import('./pages/ParticipationPolicy.jsx'));
const Finder = lazy(() => import('./pages/Finder.jsx'));
const ZoomLink = lazy(() => import('./pages/ZoomLink.jsx'));
const Feedback = lazy(() => import('./pages/Feedback.jsx'));
const NotFound = lazy(() => import('./pages/NotFound.jsx'));

// Admin panel is a separate, lazily-loaded bundle so it never weighs down the
// public site. It brings its own layout (no public header/footer).
const AdminApp = lazy(() => import('./admin/AdminApp.jsx'));

/** Lightweight, theme-matched fallback shown while a route chunk loads. */
function RouteFallback() {
  return (
    <div
      className="flex min-h-[60vh] items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Loading…</span>
      <span className="h-9 w-9 animate-spin rounded-full border-2 border-brand-500/30 border-t-brand-500" />
    </div>
  );
}

/** The public-facing website (with shared layout + visitor tracking). */
function PublicSite() {
  usePageTracking();
  return (
    <ConferenceConfigProvider>
    <Layout>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/speakers" element={<Speakers />} />
        <Route path="/publications" element={<Publications />} />
        <Route path="/call-for-papers" element={<Cfp />} />
        <Route path="/cfp" element={<Cfp />} />
        <Route path="/registration" element={<Registration />} />
        <Route path="/registration-backup" element={<Registration forceOpen />} />
        <Route path="/register" element={<OfferRegistration currency="USD" />} />
        <Route path="/register-inr" element={<OfferRegistration currency="INR" />} />
        <Route path="/register/paper" element={<AcceptedPaperRegistration />} />
        <Route path="/attendance" element={<Attendance />} />
        <Route path="/registration-form-to-ensure" element={<Attendance />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/testimonials" element={<Testimonials />} />
        <Route path="/join-us" element={<JoinUs />} />
        <Route path="/committees" element={<Committees />} />
        <Route path="/venue" element={<Venue />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/agenda" element={<Agenda />} />
        <Route path="/paper-submission" element={<PaperSubmission />} />
        <Route path="/participation-form" element={<ParticipationForm />} />
        <Route path="/author-guidelines" element={<AuthorGuidelines />} />
        <Route path="/terms-conditions" element={<Terms />} />
        <Route path="/terms-and-conditions" element={<Terms />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/legal-information" element={<LegalInformation />} />
        <Route path="/legal" element={<LegalInformation />} />
        <Route path="/participation-policy" element={<ParticipationPolicy />} />
        <Route path="/policy" element={<ParticipationPolicy />} />
        <Route path="/finder" element={<Finder />} />
        <Route path="/zoom-link" element={<ZoomLink />} />
        <Route path="/zoom" element={<ZoomLink />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </Layout>
    </ConferenceConfigProvider>
  );
}

export default function App() {
  return (
    <MotionConfig reducedMotion="user">
      <ScrollToTop />
      <Toaster
        position="top-right"
        toastOptions={{
          className: 'font-body',
          style: { borderRadius: '16px' },
        }}
        richColors
        closeButton
      />
      <Routes>
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<div className="min-h-screen bg-slate-50" />}>
              <AdminApp />
            </Suspense>
          }
        />
        <Route path="/*" element={<PublicSite />} />
      </Routes>
    </MotionConfig>
  );
}
